import { useRef } from 'react';
import type { Shipment } from '../../../lib/supabase';

interface ShipmentReceiptProps {
  shipment: Shipment;
  onClose: () => void;
}

const ShipmentReceipt = ({ shipment, onClose }: ShipmentReceiptProps) => {
  const receiptRef = useRef<HTMLDivElement>(null);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const generateReceiptHTML = () => {
    const stopoversHtml =
      shipment.stopovers && shipment.stopovers.length > 0
        ? `<tr><td style="padding:10px 16px;font-size:13px;color:#6b7280;border-bottom:1px solid #e5e7eb;width:40%;">Escales</td><td style="padding:10px 16px;font-size:13px;color:#111827;font-weight:600;border-bottom:1px solid #e5e7eb;">${shipment.stopovers.join(' &#8594; ')}</td></tr>`
        : '';

    const orderDateHtml = shipment.order_date
      ? `<tr style="background:#f9fafb;"><td style="padding:10px 16px;font-size:13px;color:#6b7280;border-bottom:1px solid #e5e7eb;width:40%;">Date de commande</td><td style="padding:10px 16px;font-size:13px;color:#111827;font-weight:600;border-bottom:1px solid #e5e7eb;">${formatDate(shipment.order_date)}</td></tr>`
      : '';

    const actualDeliveryHtml = shipment.actual_delivery_date
      ? `<tr><td style="padding:10px 16px;font-size:13px;color:#6b7280;border-bottom:1px solid #e5e7eb;width:40%;">Date de livraison réelle</td><td style="padding:10px 16px;font-size:13px;color:#111827;font-weight:600;border-bottom:1px solid #e5e7eb;">${formatDate(shipment.actual_delivery_date)}</td></tr>`
      : '';

    const productHtml = shipment.product_name
      ? `<tr style="background:#f9fafb;"><td style="padding:10px 16px;font-size:13px;color:#6b7280;border-bottom:1px solid #e5e7eb;width:40%;">Produit</td><td style="padding:10px 16px;font-size:13px;color:#111827;font-weight:600;border-bottom:1px solid #e5e7eb;">${shipment.product_name}${shipment.product_quantity ? ` (x${shipment.product_quantity})` : ''}</td></tr>`
      : '';

    const productPriceHtml = shipment.product_price
      ? `<tr><td style="padding:10px 16px;font-size:13px;color:#6b7280;border-bottom:1px solid #e5e7eb;width:40%;">Prix unitaire</td><td style="padding:10px 16px;font-size:13px;color:#111827;font-weight:600;border-bottom:1px solid #e5e7eb;">${shipment.product_price.toLocaleString()} ${shipment.currency || 'USD'}</td></tr>`
      : '';

    const paymentHtml = shipment.payment_method
      ? `<tr style="background:#f9fafb;"><td style="padding:10px 16px;font-size:13px;color:#6b7280;border-bottom:1px solid #e5e7eb;width:40%;">Mode de paiement</td><td style="padding:10px 16px;font-size:13px;color:#111827;font-weight:600;border-bottom:1px solid #e5e7eb;">${shipment.payment_method}</td></tr>`
      : '';

    const totalAmountHtml = shipment.total_amount
      ? `<tr><td style="padding:10px 16px;font-size:13px;color:#6b7280;border-bottom:1px solid #e5e7eb;width:40%;">Montant total</td><td style="padding:10px 16px;font-size:13px;color:#111827;font-weight:600;border-bottom:1px solid #e5e7eb;">${shipment.total_amount.toLocaleString()} ${shipment.currency || 'USD'}</td></tr>`
      : '';

    const senderHtml = shipment.sender_name
      ? `<tr style="background:#f9fafb;"><td style="padding:10px 16px;font-size:13px;color:#6b7280;border-bottom:1px solid #e5e7eb;width:40%;">Expéditeur</td><td style="padding:10px 16px;font-size:13px;color:#111827;font-weight:600;border-bottom:1px solid #e5e7eb;">${shipment.sender_name}${shipment.sender_phone ? ` - ${shipment.sender_phone}` : ''}<br/>${[shipment.sender_address_line1, shipment.sender_city, shipment.sender_country].filter(Boolean).join(', ')}</td></tr>`
      : '';

    const recipientHtml = shipment.recipient_name
      ? `<tr><td style="padding:10px 16px;font-size:13px;color:#6b7280;border-bottom:1px solid #e5e7eb;width:40%;">Destinataire</td><td style="padding:10px 16px;font-size:13px;color:#111827;font-weight:600;border-bottom:1px solid #e5e7eb;">${shipment.recipient_name}${shipment.recipient_phone ? ` - ${shipment.recipient_phone}` : ''}${shipment.recipient_email ? `<br/>✉ ${shipment.recipient_email}` : ''}<br/>${[shipment.recipient_address_line1, shipment.recipient_address_line2, shipment.recipient_city, shipment.recipient_postal_code, shipment.recipient_country].filter(Boolean).join(', ')}</td></tr>`
      : '';

    const contentsHtml = shipment.package_contents
      ? `<tr style="background:#f9fafb;"><td style="padding:10px 16px;font-size:13px;color:#6b7280;border-bottom:1px solid #e5e7eb;width:40%;">Contenu</td><td style="padding:10px 16px;font-size:13px;color:#111827;font-weight:600;border-bottom:1px solid #e5e7eb;">${shipment.package_contents}</td></tr>`
      : '';

    const routeText = shipment.stopovers && shipment.stopovers.length > 0
      ? `${shipment.sender_country || shipment.origin} &#8594; ${shipment.stopovers.join(' &#8594; ')} &#8594; ${shipment.recipient_country || shipment.destination}`
      : `${shipment.sender_country || shipment.origin} &#8594; ${shipment.recipient_country || shipment.destination}`;

    const stopAlertHtml = shipment.is_stopped
      ? `<div style="padding:16px 32px;background:#fef2f2;border-bottom:1px solid #fecaca;border-left:4px solid #dc2626;">
          <div style="display:flex;align-items:center;gap:10px;">
            <div style="font-size:20px;color:#dc2626;">⚠️</div>
            <div>
              <div style="font-size:14px;font-weight:600;color:#991b1b;margin-bottom:4px;">Envoi arrêté</div>
              <div style="font-size:13px;color:#7f1d1d;">${shipment.stop_reason || 'Cet envoi a été temporairement arrêté.'}</div>
            </div>
          </div>
        </div>`
      : '';

    return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>Reçu d'envoi - ${shipment.tracking_number}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Inter', sans-serif; background: #f3f4f6; }
  @media print {
    body { background: white; }
    .no-print { display: none !important; }
    .receipt-wrapper { box-shadow: none !important; margin: 0 !important; }
  }
</style>
</head>
<body>
<div style="max-width:680px;margin:32px auto;padding:0 16px;">
  <div class="no-print" style="text-align:center;margin-bottom:20px;">
    <button onclick="window.print()" style="padding:12px 32px;background:#0066CC;color:white;border:none;border-radius:8px;font-size:14px;font-weight:600;cursor:pointer;font-family:Inter,sans-serif;">
      Imprimer / Enregistrer en PDF
    </button>
    <button onclick="window.close()" style="padding:12px 32px;background:#e5e7eb;color:#374151;border:none;border-radius:8px;font-size:14px;font-weight:600;cursor:pointer;margin-left:12px;font-family:Inter,sans-serif;">
      Fermer
    </button>
  </div>

  <div class="receipt-wrapper" style="background:white;border-radius:12px;overflow:hidden;box-shadow:0 4px 24px rgba(0,0,0,0.08);position:relative;">
    
    <div style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);opacity:0.03;pointer-events:none;">
      <svg width="300" height="300" viewBox="0 0 24 24" fill="#0066CC"><path d="M8.965 18a3.5 3.5 0 0 1-6.93 0H1V6a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v2h3l3 4.056V18h-2.035a3.5 3.5 0 0 1-6.93 0h-5.07zM15 7H3v8.05a3.5 3.5 0 0 1 5.663.95h5.674c.168-.353.393-.674.663-.95V7zm2 6h4v-.285L18.992 10H17v3zm.5 6a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zm-12 0a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z"/></svg>
    </div>

    <div style="background:linear-gradient(135deg,#0066CC 0%,#004999 100%);padding:32px 32px 28px;position:relative;overflow:hidden;">
      <div style="position:absolute;top:-40px;right:-40px;width:200px;height:200px;background:rgba(255,255,255,0.05);border-radius:50%;"></div>
      <div style="position:absolute;bottom:-60px;left:-30px;width:160px;height:160px;background:rgba(255,255,255,0.03);border-radius:50%;"></div>
      <div style="display:flex;align-items:center;justify-content:space-between;position:relative;z-index:1;">
        <div>
          <div style="font-size:24px;font-weight:700;color:white;letter-spacing:-0.5px;">TrackVerse</div>
          <div style="font-size:11px;color:rgba(255,255,255,0.7);margin-top:4px;letter-spacing:1px;text-transform:uppercase;">Solutions Logistiques Mondiales</div>
        </div>
        <div style="text-align:right;">
          <div style="font-size:11px;color:rgba(255,255,255,0.7);text-transform:uppercase;letter-spacing:1px;">Reçu d'envoi</div>
          <div style="font-size:18px;font-weight:700;color:white;margin-top:4px;font-family:monospace;letter-spacing:1px;">${shipment.tracking_number}</div>
        </div>
      </div>
    </div>

    ${stopAlertHtml}

    <div style="background:#f0fdf4;padding:16px 32px;border-bottom:1px solid #e5e7eb;display:flex;align-items:center;justify-content:space-between;">
      <div style="display:flex;align-items:center;gap:10px;">
        <div style="width:10px;height:10px;border-radius:50%;background:#00CC88;"></div>
        <span style="font-size:14px;font-weight:600;color:#166534;">Statut: ${shipment.status.charAt(0).toUpperCase() + shipment.status.slice(1).replace('-', ' ')}</span>
      </div>
      <span style="font-size:12px;color:#6b7280;">Émis le: ${new Date().toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
    </div>

    <div style="padding:24px 32px;border-bottom:1px solid #e5e7eb;background:#fafbfc;">
      <div style="font-size:11px;color:#6b7280;text-transform:uppercase;letter-spacing:1px;margin-bottom:10px;">Itinéraire</div>
      <div style="font-size:15px;font-weight:600;color:#111827;">${routeText}</div>
      ${shipment.transport_mode ? `<div style="font-size:12px;color:#6b7280;margin-top:6px;">Transport par ${shipment.transport_mode === 'sea' ? 'bateau' : 'avion'}</div>` : ''}
    </div>

    <div style="padding:24px 32px;">
      <div style="font-size:11px;color:#6b7280;text-transform:uppercase;letter-spacing:1px;margin-bottom:16px;">Détails de l'envoi</div>
      <table style="width:100%;border-collapse:collapse;border:1px solid #e5e7eb;border-radius:8px;overflow:hidden;">
        <tr style="background:#f9fafb;"><td style="padding:10px 16px;font-size:13px;color:#6b7280;border-bottom:1px solid #e5e7eb;width:40%;">Origine</td><td style="padding:10px 16px;font-size:13px;color:#111827;font-weight:600;border-bottom:1px solid #e5e7eb;">${shipment.sender_country || shipment.origin}</td></tr>
        ${stopoversHtml}
        <tr><td style="padding:10px 16px;font-size:13px;color:#6b7280;border-bottom:1px solid #e5e7eb;width:40%;">Destination</td><td style="padding:10px 16px;font-size:13px;color:#111827;font-weight:600;border-bottom:1px solid #e5e7eb;">${shipment.recipient_country || shipment.destination}</td></tr>
        <tr style="background:#f9fafb;"><td style="padding:10px 16px;font-size:13px;color:#6b7280;border-bottom:1px solid #e5e7eb;width:40%;">Type de service</td><td style="padding:10px 16px;font-size:13px;color:#111827;font-weight:600;border-bottom:1px solid #e5e7eb;">${shipment.service}</td></tr>
        ${orderDateHtml}
        <tr><td style="padding:10px 16px;font-size:13px;color:#6b7280;border-bottom:1px solid #e5e7eb;width:40%;">Date d'expédition</td><td style="padding:10px 16px;font-size:13px;color:#111827;font-weight:600;border-bottom:1px solid #e5e7eb;">${formatDate(shipment.ship_date)}</td></tr>
        <tr style="background:#f9fafb;"><td style="padding:10px 16px;font-size:13px;color:#6b7280;border-bottom:1px solid #e5e7eb;width:40%;">Livraison estimée</td><td style="padding:10px 16px;font-size:13px;color:#111827;font-weight:600;border-bottom:1px solid #e5e7eb;">${formatDate(shipment.estimated_delivery)}</td></tr>
        ${actualDeliveryHtml}
        <tr><td style="padding:10px 16px;font-size:13px;color:#6b7280;border-bottom:1px solid #e5e7eb;width:40%;">Poids</td><td style="padding:10px 16px;font-size:13px;color:#111827;font-weight:600;border-bottom:1px solid #e5e7eb;">${shipment.weight} kg</td></tr>
        <tr style="background:#f9fafb;"><td style="padding:10px 16px;font-size:13px;color:#6b7280;border-bottom:1px solid #e5e7eb;width:40%;">Valeur déclarée</td><td style="padding:10px 16px;font-size:13px;color:#111827;font-weight:600;border-bottom:1px solid #e5e7eb;">${Number(shipment.value).toLocaleString()} USD</td></tr>
        ${productHtml}
        ${productPriceHtml}
        ${paymentHtml}
        ${totalAmountHtml}
        ${senderHtml}
        ${recipientHtml}
        ${contentsHtml}
      </table>
    </div>

    <div style="padding:20px 32px;background:#f9fafb;border-top:1px solid #e5e7eb;">
      <div style="display:flex;justify-content:space-between;align-items:center;">
        <div>
          <div style="font-size:11px;color:#9ca3af;">Ceci est un reçu généré automatiquement. Aucune signature requise.</div>
          <div style="font-size:11px;color:#9ca3af;margin-top:2px;">Pour toute question, contactez support@trackverse.com</div>
        </div>
        <div style="font-size:11px;color:#9ca3af;text-align:right;">
          <div>TrackVerse Logistique Mondiale</div>
          <div>www.trackverse.com</div>
        </div>
      </div>
    </div>

  </div>
</div>
</body>
</html>`;
  };

  const handleDownload = () => {
    const html = generateReceiptHTML();
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `TrackVerse-Recu-${shipment.tracking_number}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const handleOpenInNewTab = () => {
    const html = generateReceiptHTML();
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full p-8 text-center">
        <div className="w-16 h-16 flex items-center justify-center bg-[#0066CC]/10 rounded-full mx-auto mb-5">
          <i className="ri-file-download-line text-3xl text-[#0066CC]"></i>
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Télécharger le reçu</h3>
        <p className="text-gray-600 text-sm mb-2">
          Suivi: <span className="font-mono font-semibold text-[#0066CC]">{shipment.tracking_number}</span>
        </p>
        <p className="text-gray-500 text-sm mb-6">
          Téléchargez votre reçu professionnel ou ouvrez-le dans un nouvel onglet pour l'imprimer / l'enregistrer en PDF.
        </p>

        <div ref={receiptRef} className="hidden" />

        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={handleDownload}
              className="flex-1 px-6 py-3 bg-[#0066CC] text-white rounded-lg font-semibold hover:bg-[#0052A3] transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
            >
              <i className="ri-download-2-line"></i>
              Télécharger
            </button>
            <button
              onClick={handleOpenInNewTab}
              className="flex-1 px-6 py-3 bg-gray-800 text-white rounded-lg font-semibold hover:bg-gray-900 transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
            >
              <i className="ri-external-link-line"></i>
              Ouvrir &amp; Imprimer
            </button>
          </div>
          <button
            onClick={onClose}
            className="w-full px-6 py-3 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
          >
            Annuler
          </button>
        </div>
      </div>
    </div>
  );
};

export default ShipmentReceipt;
